import { CSSProperties, useEffect, useState } from 'react';
import { Box, Button, styled } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import {
  SText,
  SSelectDropdown,
  SelectDetailsButton,
  SEmptyState,
  SInfiniteScroll,
  SingleSelectLoader,
  SHighlightText,
  SButton,
  SSearchInput
} from '@setvi/shared/components';
import { distances } from '@setvi/shared/styles';

import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import eventEmitter from '@setvi/shared/libs/EventEmitter';
import { TextColor } from '@setvi/shared/enums';
import { PriceGroup } from '@setvi/shared/interfaces';
import { usePriceGroups } from './usePriceGroup';

const StyledSearchInput = styled(SSearchInput)({
  width: '100%',
  borderRadius: 99,
  margin: `${distances.px.small} 0 ${distances.px.medium} 0`,
  background: '#F8F9FA',
  padding: `${distances.px.small} ${distances.px.base}`,
  '& fieldset': {
    borderColor: '#EEF0F2 !important'
  }
});

interface PriceGroupDropdownProps {
  newDesign?: boolean;
  selectedPriceGroup: PriceGroup;
  onChange: (priceGroup: PriceGroup) => void;
  style?: { input?: CSSProperties; dropdownWrapperWidth?: number };
}

export const PriceGroupDropdown = ({
  selectedPriceGroup,
  newDesign,
  style,
  onChange
}: PriceGroupDropdownProps) => {
  const [anchor, setAnchor] = useState(null);
  const [search, setSearch] = useState('');
  const { priceGroups, loading, fetchMore } = usePriceGroups({
    searchStr: search
  });

  const handleSelectCustomer = (priceGroup: PriceGroup) => {
    onChange(priceGroup);
    setAnchor(null);
  };

  const isSelected = (priceGroup: PriceGroup) =>
    selectedPriceGroup?._id === priceGroup._id;

  useEffect(() => {
    eventEmitter.on('sdropdown-menu-opened', () => setAnchor(null));

    return () => {
      eventEmitter.off('sdropdown-menu-opened', () => setAnchor(null));
    };
  }, []);

  return (
    <SSelectDropdown
      newDesign={newDesign}
      anchor={
        <SelectDetailsButton
          newDesign={newDesign}
          width={250}
          style={style?.input}
          noBorder
          title={
            selectedPriceGroup ? selectedPriceGroup.name : `Select Price Group`
          }
          titleHighligted={!!selectedPriceGroup}
          onClick={e => setAnchor(e?.currentTarget)}
          open={Boolean(anchor)}
        />
      }
      disablePortal
      anchorElement={anchor}
      onClose={() => setAnchor(null)}
      content={
        <Box width={style?.dropdownWrapperWidth || 285}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            <SText
              lineHeight="24px"
              weight="semibold"
              title="Select Price Group"
            />
            {selectedPriceGroup && (
              <SButton
                variant="text"
                style={{
                  minHeight: 'max-content',
                  padding: '2px 8px',
                  fontSize: 12
                }}
                startIcon={<CloseIcon />}
                onClick={() => {
                  onChange(null);
                  setAnchor(null);
                }}>
                Clear
              </SButton>
            )}
          </Box>
          <StyledSearchInput
            defaultValue={search}
            placeholder="Search Price Group..."
            searchIconSize="14px"
            onChange={val => setSearch(val)}
          />
          <Box height={300} overflow="auto">
            <Box>
              <SInfiniteScroll
                list={priceGroups}
                keyGetter={priceGroup => priceGroup._id}
                fetchMore={fetchMore}
                render={priceGroup => (
                  <Button
                    fullWidth
                    style={{
                      display: 'flex',
                      justifyContent: 'start',
                      borderRadius: distances.px.base,
                      padding: `${distances.px.xsmall} ${distances.px.base}`,
                      marginBottom: distances.px.xsmall,
                      background: isSelected(priceGroup) && '#EBF5FF',
                      color: isSelected(priceGroup) ? '#2196F3' : undefined
                    }}
                    onClick={() => handleSelectCustomer(priceGroup)}
                    endIcon={
                      <CheckRoundedIcon
                        htmlColor={
                          isSelected(priceGroup)
                            ? TextColor.Blue
                            : 'transparent'
                        }
                      />
                    }
                    variant="text"
                    color="default">
                    <Box
                      display="flex"
                      flexDirection="column"
                      width="100%"
                      gridGap="4px">
                      <SHighlightText
                        value={priceGroup.name}
                        searchQuery={search}
                        fontColor={
                          isSelected(priceGroup) ? TextColor.Blue : '#20262D'
                        }
                        lineHeight="24px"
                        weight="bold"
                      />
                      <SText
                        size="xs"
                        weight="bold"
                        lineHeight="14px"
                        fontColor={TextColor.Blue}
                        title={priceGroup._id}
                      />
                    </Box>
                  </Button>
                )}
              />
            </Box>
            {loading && <SingleSelectLoader />}
            {!loading && priceGroups?.length === 0 && (
              <SEmptyState subTitle="No Price Group found." />
            )}
          </Box>
        </Box>
      }
    />
  );
};
